import {
	Container,
	CreateFormContainer,
	EmptyState,
	FormActions,
	FormField,
	Header,
	Input,
	Label,
	SearchContainer,
	SearchIcon,
	SearchInput,
	TagColorPreview,
	TagDescription,
	TagInfo,
	TagItem,
	TagName,
	TagsList,
	TextArea,
	Title,
} from './TagSelection.styles';
import { CreateTagRequest, TagType } from 'features/Dashboard/components/tixologist/TagsSection/types';
import { FC, useMemo, useState } from 'react';
import { PUNCHUP_QUATERNARY, PUNCHUP_TERTIARY } from 'features/common/components/Button/constants';
import { addTagsToEvent, createTag } from 'features/Dashboard/components/tixologist/TagsSection/services';
import { useHistory, useParams } from 'react-router-dom';

import { Button } from 'features/common/components/Button';
import { IdParamsType } from 'utils/types';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { ROUTES } from 'features/routes';
import { TYPES } from 'hooks/useToastNotification/constants';
import { Tag } from 'features/common/types';
import { useAppSelector } from 'store/hooks';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTagsApi } from 'features/Dashboard/components/tixologist/TagsSection/hooks/useTagsApi';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';

const MAX_TAGS = 10;

const TagSelection: FC = () => {
	const { t } = useTranslation();
	const { goBack, push } = useHistory();
	const { id } = useParams<IdParamsType>();
	const { showNotification } = useToastNotification();
	const { tags: availableTags, refetch: refetchTags } = useTagsApi();
	const { event } = useGetEvent();
	const { isPunchup } = useAppSelector((state) => state.auth);
	const [searchQuery, setSearchQuery] = useState('');
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [newTagName, setNewTagName] = useState('');
	const [newTagDescription, setNewTagDescription] = useState('');
	const [newTagColor, setNewTagColor] = useState('#F5F5F5');

	const eventTags = event?.tags || [];
	const normalizedTags = eventTags.map((tag) => ({
		...tag,
		id: tag.id || (tag as any).ID,
	}));
	const selectedTagIds = normalizedTags.map((tag) => tag.id);
	const canAddMore = selectedTagIds.length < MAX_TAGS;

	const filteredTags = useMemo(() => {
		if (!Array.isArray(availableTags)) return [];
		return availableTags
			.filter((tag) => tag.tag_type === TagType.EVENT)
			.filter((tag) => tag.name.toLowerCase().includes(searchQuery.toLowerCase()))
			.map((tag) => ({
				...tag,
				id: tag.id || (tag as any).ID,
			}));
	}, [availableTags, searchQuery]);

	const handleSelectTag = async (tag: Tag) => {
		if (selectedTagIds.includes(tag.id)) {
			showNotification({ message: t('tags-already-added'), type: TYPES.INFO });
			return;
		}

		if (!canAddMore) {
			showNotification({ message: t('tags-max-limit-reached'), type: TYPES.INFO });
			return;
		}

		try {
			await addTagsToEvent(id, { tag_ids: [tag.id] });
			showNotification({ message: t('tags-added-successfully'), type: TYPES.SUCCESS });
			push(ROUTES.DASHBOARD_PATH.replace(':id', id));
		} catch (error) {
			showNotification({ message: t('tags-add-error'), type: TYPES.ERROR });
		}
	};

	const handleCreateNew = () => {
		setShowCreateForm(!showCreateForm);
		// Reset form when opening
		if (!showCreateForm) {
			setNewTagName('');
			setNewTagDescription('');
			setNewTagColor('#F5F5F5');
		}
	};

	const handleSubmitNewTag = async () => {
		if (!newTagName.trim()) {
			showNotification({ message: t('tags-name-required'), type: TYPES.ERROR });
			return;
		}

		try {
			const tagData: CreateTagRequest = {
				name: newTagName.trim(),
				description: newTagDescription.trim() || undefined,
				color: newTagColor,
				tag_type: TagType.EVENT,
			};

			const newTag = await createTag(tagData);
			await refetchTags();
			showNotification({ message: t('tags-created-successfully'), type: TYPES.SUCCESS });

			// Add the newly created tag to the event
			await addTagsToEvent(id, { tag_ids: [newTag.id] });
			showNotification({ message: t('tags-added-successfully'), type: TYPES.SUCCESS });

			// Navigate back to dashboard
			push(ROUTES.DASHBOARD_PATH.replace(':id', id));
		} catch (error) {
			showNotification({ message: t('tags-create-error'), type: TYPES.ERROR });
		}
	};

	const handleCancelCreate = () => {
		setShowCreateForm(false);
		setNewTagName('');
		setNewTagDescription('');
		setNewTagColor('#F5F5F5');
	};

	const onCancel = () => {
		goBack();
	};
	const content = (
		<Container>
			<Header>
				<Title>{t('tags-select-tags')}</Title>
			</Header>

			<SearchContainer>
				<SearchIcon>
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<circle cx="11" cy="11" r="8" />
						<path d="M21 21l-4.35-4.35" />
					</svg>
				</SearchIcon>
				<SearchInput
					type="text"
					placeholder={t('tags-search-placeholder')}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
				/>
			</SearchContainer>

			{filteredTags.length > 0 ? (
				<>
					<TagsList>
						{filteredTags.map((tag) => {
							const isSelected = selectedTagIds.includes(tag.id);
							const isDisabled = !isSelected && !canAddMore;

							return (
								<TagItem
									key={tag.id}
									$selected={isSelected}
									onClick={() => !isDisabled && handleSelectTag(tag)}
									style={{
										opacity: isDisabled ? 0.5 : 1,
										cursor: isDisabled ? 'not-allowed' : 'pointer',
									}}
								>
									<TagColorPreview $color={tag.color} />
									<TagInfo>
										<TagName>{tag.name}</TagName>
										{tag.description && <TagDescription>{tag.description}</TagDescription>}
									</TagInfo>
									{isSelected && <span>✓</span>}
								</TagItem>
							);
						})}
					</TagsList>
					<PunchupButton
						type="button"
						variant="secondary"
						text={t('button-cancel')}
						onClick={onCancel}
						style={{ flex: 1 }}
					/>
					<PunchupButton type="button" variant="primary" text={t('button-create-new')} style={{ flex: 1 }} />
				</>
			) : (
				<>
					<EmptyState>{t('tags-no-tags-found')}</EmptyState>
					<FormActions>
						<PunchupButton
							type="button"
							variant="secondary"
							text={t('button-cancel')}
							onClick={onCancel}
							style={{ flex: 1 }}
						/>
						<PunchupButton
							type="button"
							variant="primary"
							onClick={handleCreateNew}
							text={t('button-create-new')}
							style={{ flex: 1 }}
						/>
					</FormActions>
				</>
			)}

			{showCreateForm && (
				<CreateFormContainer>
					<FormField>
						<Label>
							{t('tags-name')} <span style={{ color: 'red' }}>*</span>
						</Label>
						<Input
							type="text"
							placeholder={t('tags-name-placeholder')}
							value={newTagName}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTagName(e.target.value)}
							maxLength={50}
						/>
					</FormField>

					<FormField>
						<Label>{t('tags-description')}</Label>
						<TextArea
							placeholder={t('tags-description-placeholder')}
							value={newTagDescription}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewTagDescription(e.target.value)}
							maxLength={200}
						/>
					</FormField>

					<FormActions>
						<Button text={t('cancel')} category={PUNCHUP_QUATERNARY} onClick={handleCancelCreate} className="w-[50%]" />
						<Button
							text={t('tags-create-add')}
							category={isPunchup ? PUNCHUP_TERTIARY : undefined}
							onClick={() => handleSubmitNewTag()}
							disabled={!newTagName.trim()}
							type="submit"
						/>
					</FormActions>
				</CreateFormContainer>
			)}
		</Container>
	);

	if (isPunchup) {
		return (
			<PunchupPageShell pageName="Tags" onBack={onCancel}>
				{content}
			</PunchupPageShell>
		);
	}

	return content;
};

export { TagSelection };
